/**
 * @component Main
 * @description
 * Componente principal que inicializa y proporciona el contexto global de configuración (`AppContext`)
 * a través de un reducer. Controla opciones globales como tema oscuro, diseño fluido, modo RTL, etc.
 * 
 * También se encarga de cargar estilos dinámicos como RTL/Dark antes de renderizar el resto de la app.
 * 
 * @example
 * <Main>
 *   <App />
 * </Main>
 */

import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import AppContext from './config/app-context';
import { settings } from './config';
import { getColor, getItemFromStore } from './helpers/utils';
import { configReducer } from './reducers/configReducer';
import useToggleStyle from './hooks/useToggleStyle';

import { Chart as ChartJS, registerables } from 'chart.js';
ChartJS.register(...registerables); // Registrar todos los módulos de Chart.js

const Main = (props) => {

	/**
	 * Estado inicial del contexto de configuración.
	 * Se carga desde `localStorage` (por medio de `getItemFromStore`) o del archivo de configuración.
	 */
	const configState = {
		isFluid: true,
		isRTL: getItemFromStore('isRTL', settings.isRTL),
		isDark: getItemFromStore('isDark', settings.isDark),
		navbarPosition: getItemFromStore('navbarPosition', settings.navbarPosition),
		disabledNavbarPosition: [],
		isNavbarVerticalCollapsed: getItemFromStore('isNavbarVerticalCollapsed', settings.isNavbarVerticalCollapsed),
		navbarStyle: getItemFromStore('navbarStyle', settings.navbarStyle),
		currency: settings.currency,
		showBurgerMenu: settings.showBurgerMenu,
		showSettingPanel: false,
		navbarCollapsed: false,
		isFullscreen: false
	};

	/**
	 * Reducer para manejar las actualizaciones de configuración global.
	 */
	const [config, configDispatch] = useReducer(configReducer, configState);

	/**
	 * Hook personalizado para aplicar estilos RTL o Dark dinámicamente.
	 * @returns {boolean} isLoaded - Indica si los estilos han sido aplicados correctamente.
	 */
	const { isLoaded } = useToggleStyle(config.isRTL, config.isDark, configDispatch);

	/**
	 * setConfig
	 * Actualiza el valor de una clave del estado de configuración global.
	 *
	 * @param {string} key - Nombre del parámetro a actualizar.
	 * @param {any} value - Nuevo valor a asignar.
	 */
	const setConfig = (key, value) => {
		configDispatch({
			type: 'SET_CONFIG',
			payload: {
				key,
				value,
				setInStore: [
					'isFluid',
					'isRTL',
					'isDark',
					'navbarPosition',
					'isNavbarVerticalCollapsed',
					'navbarStyle'
				].includes(key)
			}
		});
	};

	// Si aún no se han aplicado los estilos (dark o RTL), renderiza un fondo temporal
	if (!isLoaded) {
		return (
			<div
				style={{
					position: 'fixed',
					top: 0,
					right: 0,
					bottom: 0,
					left: 0,
					backgroundColor: config.isDark ? getColor('dark') : getColor('light')
				}}
			/>
		);
	}

	/**
	 * AppContext.Provider
	 * Provee el estado global y sus métodos a toda la aplicación.
	 */
	return (
		<AppContext.Provider value={{ config, setConfig, configDispatch }}>
			{props.children}
		</AppContext.Provider>
	);
};

Main.propTypes = {
	/** Elementos hijos que recibirán el contexto */
	children: PropTypes.node
};

export default Main;